import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Avatar,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grow,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import VerifiedUserOutlinedIcon from "@material-ui/icons/VerifiedUserOutlined";
import PersonAddOutlinedIcon from "@material-ui/icons/PersonAddOutlined";
import HistoryOutlinedIcon from "@material-ui/icons/HistoryOutlined";
import AppsOutlinedIcon from "@material-ui/icons/AppsOutlined";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import AccountMenuPanel from "../components/Header/AccountMenuPanel";
import usePermissions from "../utils/permissions";
import { useEffectiveProfile } from "../utils/useEffectiveProfile";
import { useSnackbar } from "../utils/useSnackbar";
import * as api from "../api";

// The till's account button: the same pill + menu as the normal header, for the POS top bar.
const useStyles = makeStyles((theme) => ({
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    marginLeft: 4,
    padding: "3px 10px 3px 3px !important",
    borderRadius: 999,
    textTransform: "none",
    color: "#ffffff",
    background: "rgba(255, 255, 255, 0.16)",
    border: "1px solid rgba(255, 255, 255, 0.45)",
    transition: "background .15s ease, box-shadow .15s ease",
    "&:hover": { background: "rgba(255, 255, 255, 0.26)", boxShadow: "0 2px 8px rgba(0,0,0,.12)" },
    [theme.breakpoints.down("sm")]: { padding: "2px !important", gap: 0 },
  },
  avatar: { width: 32, height: 32, backgroundColor: "#fff", color: "#ea580c", fontWeight: 800, fontSize: "0.9rem", border: "2px solid rgba(255,255,255,.9)" },
  name: {
    maxWidth: 120,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.86rem",
    fontWeight: 700,
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  chev: { fontSize: 20, transition: "transform .2s ease", [theme.breakpoints.down("sm")]: { display: "none" } },
  paper: { borderRadius: 14, overflow: "hidden", border: "1px solid #e2e8f0", boxShadow: "0 10px 30px rgba(0,0,0,.18)" },
}));

const PosUserMenu = ({ onLogout }) => {
  const classes = useStyles();
  const history = useHistory();
  const [openSnackbar] = useSnackbar();
  const profile = useEffectiveProfile();
  const { canReadPage, isAdmin } = usePermissions();
  const adminNow = isAdmin();
  const user = JSON.parse(localStorage.getItem("profile") || "null");

  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const close = () => setAnchor(null);
  const go = (path) => {
    close();
    history.push(`/${path}`);
  };

  const isSuperAdminUser = Array.isArray(user?.result?.userType)
    ? user.result.userType.includes("superAdmin")
    : user?.result?.userType === "superAdmin";
  const goSuperAdmin = async () => {
    try {
      await api.recordProgramLogEvent({ event: "SWITCH_TO_SUPERADMIN" });
    } catch (error) {
      console.warn("Could not record the SuperAdmin view switch", error);
    }
    localStorage.removeItem("superAdminViewingPos");
    window.location.href = "/superadmin";
  };

  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [busy, setBusy] = useState(false);
  const closePw = () => {
    setPwOpen(false);
    setPw("");
    setPw2("");
    setShow1(false);
    setShow2(false);
  };
  const submitPw = async () => {
    if (!pw || pw.length < 6) return openSnackbar("New password must be at least 6 characters");
    if (pw !== pw2) return openSnackbar("New password and confirmation don't match");
    setBusy(true);
    try {
      await api.changeMyPassword(pw);
      openSnackbar("Password updated successfully");
      closePw();
    } catch (err) {
      openSnackbar(err?.response?.data?.message || "Failed to update password");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  const eye = (shown, toggle) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={toggle} edge="end" size="small">
          {shown ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </InputAdornment>
    ),
  });

  return (
    <>
      <Button
        aria-haspopup="true"
        aria-label="Account menu"
        aria-expanded={open}
        onClick={(e) => setAnchor(open ? null : e.currentTarget)}
        className={classes.pill}
      >
        <Avatar className={classes.avatar}>{user?.result?.name?.charAt(0)}</Avatar>
        <span className={classes.name}>{user?.result?.name}</span>
        <ExpandMoreIcon className={classes.chev} style={{ transform: open ? "rotate(180deg)" : "none" }} />
      </Button>

      <Popper open={open} anchorEl={anchor} role={undefined} transition placement="bottom-end" style={{ zIndex: 1400 }}>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "right top" }}>
            <Paper elevation={6} className={classes.paper}>
              <ClickAwayListener onClickAway={close}>
                <AccountMenuPanel
                  user={user}
                  onKeyDown={(e) => e.key === "Escape" && close()}
                  onGuide={() => go("help")}
                  onChangePassword={() => {
                    close();
                    setPwOpen(true);
                  }}
                  onLogout={() => {
                    close();
                    onLogout();
                  }}
                  onSuperAdmin={isSuperAdminUser ? goSuperAdmin : undefined}
                  sections={[
                    {
                      label: "Account",
                      items: [
                        { key: "account", label: "My Account", icon: <PersonOutlineIcon />, onClick: () => go("account") },
                        { key: "settings", label: "Store Settings", icon: <SettingsOutlinedIcon />, onClick: () => go("settings") },
                        { key: "privacy", label: "Privacy Policy", icon: <VerifiedUserOutlinedIcon />, onClick: () => go("Webview/PrivacyPolicy") },
                        { key: "features", label: "All Features", icon: <AppsOutlinedIcon />, onClick: () => go("help/features") },
                      ],
                    },
                    {
                      label: "Manage",
                      items: [
                        ...(adminNow || canReadPage("Dashboard") ? [{ key: "dashboard", label: "Dashboard", icon: <DashboardOutlinedIcon />, onClick: () => go("dashboard") }] : []),
                        ...(adminNow ? [{ key: "adduser", label: "Add User", icon: <PersonAddOutlinedIcon />, onClick: () => go("adminDashboard") }] : []),
                        ...(adminNow && profile?.activityLogsVisible !== false ? [{ key: "logs", label: "Activity Logs", icon: <HistoryOutlinedIcon />, onClick: () => go("program-logs") }] : []),
                      ],
                    },
                  ]}
                />
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <Dialog open={pwOpen} onClose={closePw} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 16, overflow: "hidden" } }}>
        <DialogTitle style={{ backgroundImage: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)", color: "#fff", display: "flex", alignItems: "center", gap: 10, padding: "16px 24px" }}>
          <VpnKeyOutlinedIcon fontSize="small" />
          <span style={{ fontWeight: 700, fontSize: "1.05rem" }}>Change Password</span>
        </DialogTitle>
        <DialogContent style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
          <TextField
            autoFocus
            label="New Password"
            variant="outlined"
            size="small"
            type={show1 ? "text" : "password"}
            fullWidth
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            helperText="At least 6 characters"
            InputProps={eye(show1, () => setShow1(!show1))}
          />
          <TextField
            label="Confirm New Password"
            variant="outlined"
            size="small"
            type={show2 ? "text" : "password"}
            fullWidth
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            InputProps={eye(show2, () => setShow2(!show2))}
          />
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px 20px", gap: 8 }}>
          <Button onClick={closePw} style={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}>
            Cancel
          </Button>
          <Button
            onClick={submitPw}
            variant="contained"
            disabled={busy}
            style={{ backgroundImage: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)", color: "#fff", textTransform: "none", fontWeight: 700, borderRadius: 8 }}
          >
            {busy ? "Updating..." : "Update Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PosUserMenu;
